import { Link } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { LoPublicFormationBundleCard, LoPublicFormationCard, LoPublicPhysicalProductCard, LoPublicSiteBlock } from '@/lib/lyrraone-public';
import type { LoSiteBlock } from '@/lib/lyrraone-site-blocks';
import { resolveSiteImageUrl } from '@/components/common/ImageSourcePicker';
import { LoPublicFormationGrid } from '@/components/lyrraone/LoPublicFormationGrid';
import { LoPublicProductGrid } from '@/components/lyrraone/LoPublicProductGrid';
import {
  elementStyleClass,
  elementStyleToCss,
  getBlockElementStyle,
} from '@/lib/lyrraone-site-elements';
import { getLoSiteThemeProfile } from '@/lib/lyrraone-site-theme-profiles';
import { LoSiteThemedHero } from '@/components/lyrraone/site-themes/LoSiteThemedHero';
import { LoSiteThemedFeatures } from '@/components/lyrraone/site-themes/LoSiteThemedFeatures';
import { LoSiteThemedTestimonials } from '@/components/lyrraone/site-themes/LoSiteThemedTestimonials';
import { LoSiteThemedFaq } from '@/components/lyrraone/site-themes/LoSiteThemedFaq';
import { LoSiteThemedCta } from '@/components/lyrraone/site-themes/LoSiteThemedCta';
import { LoSiteThemedColumns } from '@/components/lyrraone/site-themes/LoSiteThemedColumns';
import { LoSiteStructuredTextView } from '@/components/lyrraone/LoSiteStructuredTextView';
import { isLoSiteStructuredTextBody } from '@/lib/lyrraone-structured-text';
import { formatPriceEuros } from '@/lib/store-formation';
import { loPublicSitePath } from '@/lib/lyrraone-custom-domain';
import { getLeBrefVideoPresentation } from '@/lib/video-block';
import { videoApi } from '@/api/video.api';
import { cn } from '@/lib/utils';

interface EditableWrapProps {
  elementKey: string;
  editMode?: boolean;
  selectedElementKey?: string | null;
  onSelectElement?: (key: string) => void;
  onInlineTextChange?: (key: string, value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  as?: keyof JSX.IntrinsicElements;
  children: React.ReactNode;
  editableText?: boolean;
}

function EditableWrap({
  elementKey,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  className,
  style,
  as: Tag = 'div',
  children,
  editableText,
}: EditableWrapProps) {
  const [editing, setEditing] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const selected = editMode && selectedElementKey === elementKey;

  useEffect(() => {
    if (!editing) return;
    ref.current?.focus();
  }, [editing]);

  const handleBlur = useCallback(() => {
    if (!editing || !ref.current) return;
    setEditing(false);
    const text = ref.current.innerText.trim();
    onInlineTextChange?.(elementKey, text);
  }, [editing, elementKey, onInlineTextChange]);

  const handleClick = (e: React.MouseEvent) => {
    if (!editMode) return;
    e.stopPropagation();
    onSelectElement?.(elementKey);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (!editMode || !editableText) return;
    e.stopPropagation();
    onSelectElement?.(elementKey);
    setEditing(true);
  };

  const props: Record<string, unknown> = {
    'data-lo-element': elementKey,
    className: cn(
      className,
      editMode && 'lo-site-editable',
      selected && 'lo-site-element-selected',
    ),
    style,
    onClick: handleClick,
    onDoubleClick: handleDoubleClick,
  };

  if (editMode && editableText && editing) {
    props.contentEditable = true;
    props.suppressContentEditableWarning = true;
    props.onBlur = handleBlur;
    props.onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        ref.current?.blur();
      }
      if (e.key === 'Escape') {
        setEditing(false);
      }
    };
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <Tag ref={ref as never} {...props}>
      {children}
    </Tag>
  );
}

export interface LoSiteBlockViewProps {
  block: LoPublicSiteBlock | LoSiteBlock;
  blockKey: string;
  formations: LoPublicFormationCard[];
  bundles?: LoPublicFormationBundleCard[];
  physicalProducts?: LoPublicPhysicalProductCard[];
  siteSlug: string;
  customDomainSlug?: string | null;
  siteTheme?: string | null;
  siteLayoutTheme?: string | null;
  editMode?: boolean;
  selectedElementKey?: string | null;
  onSelectElement?: (elementKey: string) => void;
  onInlineTextChange?: (elementKey: string, value: string) => void;
  linkContext?: import('@/lib/lyrraone-block-links').LoBlockLinkContext;
  creatorName?: string | null;
}

export function LoSiteBlockView({
  block,
  blockKey,
  formations,
  bundles = [],
  physicalProducts = [],
  siteSlug,
  customDomainSlug,
  siteTheme,
  siteLayoutTheme,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  linkContext,
  creatorName,
}: LoSiteBlockViewProps) {
  const loBlock = block as LoSiteBlock;
  const styleOf = (key: string) => getBlockElementStyle(loBlock, key);
  const themeProfile = getLoSiteThemeProfile(siteTheme, siteLayoutTheme);
  const themedShared = {
    block: loBlock,
    blockKey,
    siteSlug,
    customDomainSlug,
    editMode,
    selectedElementKey,
    onSelectElement,
    onInlineTextChange,
    styleOf,
    linkContext,
  };

  switch (block.type) {
    case 'hero':
      return (
        <LoSiteThemedHero
          layout={themeProfile.hero}
          {...themedShared}
        />
      );
    case 'heading':
      return (
        <section key={blockKey} className="lo-wrap py-8 text-center">
          <EditableWrap
            elementKey="heading.title"
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            className={cn('lo-h2', elementStyleClass(styleOf('heading.title')))}
            style={elementStyleToCss(styleOf('heading.title'))}
            as="h2"
            editableText
          >
            {block.title ?? 'Titre de section'}
          </EditableWrap>
        </section>
      );
    case 'subheading':
      return (
        <section key={blockKey} className="lo-wrap py-4 text-center">
          <EditableWrap
            elementKey="subheading.title"
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            className={cn(
              'text-xl font-semibold text-[var(--lo-navy)] sm:text-2xl',
              elementStyleClass(styleOf('subheading.title')),
            )}
            style={elementStyleToCss(styleOf('subheading.title'))}
            as="h3"
            editableText
          >
            {block.title ?? 'Sous-titre'}
          </EditableWrap>
        </section>
      );
    case 'paragraph':
      return (
        <section key={blockKey} className="lo-wrap py-4">
          <EditableWrap
            elementKey="paragraph.body"
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            className={cn(
              'text-base leading-relaxed text-gray-700',
              elementStyleClass(styleOf('paragraph.body')),
            )}
            style={elementStyleToCss(styleOf('paragraph.body'))}
            as="p"
            editableText
          >
            {block.body ?? ''}
          </EditableWrap>
        </section>
      );
    case 'columns2':
    case 'columns3': {
      const colCount = block.type === 'columns2' ? 2 : 3;
      return (
        <LoSiteThemedColumns
          layout={themeProfile.columns}
          colCount={colCount as 2 | 3}
          {...themedShared}
        />
      );
    }
    case 'cta':
      return (
        <LoSiteThemedCta
          layout={themeProfile.cta}
          {...themedShared}
        />
      );
    case 'text': {
      const body = block.body ?? '';
      const isStructured = isLoSiteStructuredTextBody(body);
      if (isStructured && editMode) {
        return (
          <LoSiteStructuredTextView
            body={body}
            editMode
            selected={selectedElementKey === 'text.body'}
            className={cn(elementStyleClass(styleOf('text.body')))}
            style={elementStyleToCss(styleOf('text.body'))}
            onSelect={() => onSelectElement?.('text.body')}
            onChange={(html) => onInlineTextChange?.('text.body', html)}
          />
        );
      }
      const isRichSection = isStructured;
      return (
        <EditableWrap
          elementKey="text.body"
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          className={cn(
            isRichSection
              ? 'lo-site-rich-section eduflow-rich-html'
              : 'lo-wrap py-8 prose prose-lg max-w-none dark:prose-invert eduflow-rich-html',
            elementStyleClass(styleOf('text.body')),
          )}
          style={elementStyleToCss(styleOf('text.body'))}
          as="section"
        >
          <div dangerouslySetInnerHTML={{ __html: body }} />
        </EditableWrap>
      );
    }
    case 'features':
      return (
        <LoSiteThemedFeatures
          layout={themeProfile.features}
          {...themedShared}
        />
      );
    case 'testimonials':
      return (
        <LoSiteThemedTestimonials
          {...themedShared}
        />
      );
    case 'faq':
      return (
        <LoSiteThemedFaq
          {...themedShared}
        />
      );
    case 'formations':
      return (
        <section key={blockKey} id="formations" className="lo-wrap py-12">
          <EditableWrap
            elementKey="formations.title"
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            className={cn('lo-h2 mb-8 text-center', elementStyleClass(styleOf('formations.title')))}
            style={elementStyleToCss(styleOf('formations.title'))}
            as="h2"
            editableText
          >
            {block.title ?? 'Mes formations'}
          </EditableWrap>
          <LoPublicFormationGrid
            formations={formations}
            siteSlug={siteSlug}
            customDomainSlug={customDomainSlug}
            creatorName={creatorName}
          />
        </section>
      );
    case 'bundles':
      return (
        <section key={blockKey} id="bundles" className="lo-wrap py-12">
          <EditableWrap
            elementKey="bundles.title"
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            className={cn('lo-h2 mb-8 text-center', elementStyleClass(styleOf('bundles.title')))}
            style={elementStyleToCss(styleOf('bundles.title'))}
            as="h2"
            editableText
          >
            {block.title ?? 'Nos packs'}
          </EditableWrap>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {bundles.map((bundle) => (
              <article key={bundle.id} className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                <h3 className="font-semibold text-[var(--lo-navy)]">{bundle.title}</h3>
                {bundle.description ? (
                  <p className="mt-1 line-clamp-2 text-sm text-gray-600">{bundle.description}</p>
                ) : null}
                <p className="mt-2 text-sm text-gray-500">{bundle.formationCount} formations</p>
                <p className="mt-2 font-medium text-primary-600">{formatPriceEuros(bundle.price)}</p>
                <Link
                  to={bundle.funnelSlug
                    ? `/f/${encodeURIComponent(bundle.funnelSlug)}/checkout`
                    : loPublicSitePath(siteSlug, { formations: true, bundleId: bundle.id }, customDomainSlug)}
                  className="mt-4 inline-block text-sm font-medium text-primary-600 hover:underline"
                >
                  {bundle.funnelSlug ? 'Acheter le pack' : 'Voir le pack'}
                </Link>
              </article>
            ))}
          </div>
        </section>
      );
    case 'products':
      return (
        <section key={blockKey} id="boutique" className="lo-wrap py-12">
          <EditableWrap
            elementKey="products.title"
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            className={cn('lo-h2 mb-8 text-center', elementStyleClass(styleOf('products.title')))}
            style={elementStyleToCss(styleOf('products.title'))}
            as="h2"
            editableText
          >
            {block.title ?? 'Boutique'}
          </EditableWrap>
          <LoPublicProductGrid
            products={physicalProducts}
            siteSlug={siteSlug}
            customDomainSlug={customDomainSlug}
          />
        </section>
      );
    case 'image':
      return block.imageUrl ? (
        <section key={blockKey} className="lo-wrap py-8 text-center">
          <img
            src={resolveSiteImageUrl(block.imageUrl)}
            alt={block.alt ?? ''}
            className="mx-auto max-h-[480px] rounded-xl object-contain"
          />
          {block.caption && (
            <EditableWrap
              elementKey="image.caption"
              editMode={editMode}
              selectedElementKey={selectedElementKey}
              onSelectElement={onSelectElement}
              onInlineTextChange={onInlineTextChange}
              className={cn('mt-2 text-sm text-gray-500', elementStyleClass(styleOf('image.caption')))}
              style={elementStyleToCss(styleOf('image.caption'))}
              as="p"
              editableText
            >
              {block.caption}
            </EditableWrap>
          )}
        </section>
      ) : null;
    case 'formFields':
      return (
        <section key={blockKey} className="lo-wrap py-10">
          <div className="mx-auto max-w-xl rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center dark:border-slate-600 dark:bg-slate-900/40">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Formulaire</p>
            <p className="mt-1 text-xs text-slate-500">
              Emplacement des champs CRM (aperçu éditeur).
            </p>
          </div>
        </section>
      );
    case 'video': {
      const videoUrl = (block.url ?? '').trim();
      if (!videoUrl) {
        return (
          <section key={blockKey} className="lo-wrap py-8">
            <div className="mx-auto flex aspect-video max-w-3xl items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-900/40">
              Ajoutez une vidéo (YouTube, Vimeo ou fichier)
            </div>
          </section>
        );
      }
      const presentation = getLeBrefVideoPresentation(videoUrl);
      const nativeSrc = presentation.nativeSrc.startsWith('/uploads/')
        ? (videoApi.resolveUrl(presentation.nativeSrc) || presentation.nativeSrc)
        : presentation.nativeSrc;
      return (
        <section key={blockKey} className="lo-wrap py-8 text-center">
          <div className="relative mx-auto aspect-video max-w-3xl overflow-hidden rounded-xl bg-black">
            {presentation.mode === 'youtube' || presentation.mode === 'vimeo' ? (
              <iframe
                src={presentation.embedSrc}
                title={block.caption || 'Vidéo'}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <video
                src={nativeSrc}
                className="absolute inset-0 h-full w-full object-contain"
                controls
                playsInline
              />
            )}
          </div>
          {block.caption ? (
            <EditableWrap
              elementKey="video.caption"
              editMode={editMode}
              selectedElementKey={selectedElementKey}
              onSelectElement={onSelectElement}
              onInlineTextChange={onInlineTextChange}
              className={cn('mt-2 text-sm text-gray-500', elementStyleClass(styleOf('video.caption')))}
              style={elementStyleToCss(styleOf('video.caption'))}
              as="p"
              editableText
            >
              {block.caption}
            </EditableWrap>
          ) : null}
        </section>
      );
    }
    default:
      return null;
  }
}

export function renderPublicSiteBlock(
  block: LoPublicSiteBlock,
  key: string,
  formations: LoPublicFormationCard[],
  siteSlug: string,
  customDomainSlug?: string | null,
  siteTheme?: string | null,
  bundles?: LoPublicFormationBundleCard[],
  linkContext?: import('@/lib/lyrraone-block-links').LoBlockLinkContext,
  creatorName?: string | null,
  siteLayoutTheme?: string | null,
  physicalProducts?: LoPublicPhysicalProductCard[],
) {
  return (
    <LoSiteBlockView
      key={key}
      block={block}
      blockKey={key}
      formations={formations}
      bundles={bundles}
      physicalProducts={physicalProducts}
      siteSlug={siteSlug}
      customDomainSlug={customDomainSlug}
      siteTheme={siteTheme}
      siteLayoutTheme={siteLayoutTheme}
      linkContext={linkContext}
      creatorName={creatorName}
    />
  );
}
